import { jwtDecode } from "jwt-decode";

/**
 * Render-gate for the AI-generated clinical history UI.
 *
 * Returns true ONLY when BOTH conditions hold:
 *   1. The user's JWT carries `features.AIHistory === true`
 *   2. The user's role array includes "Practitioner" OR "SystemAdmin"
 *      (SystemAdmin gets visibility for support/debugging: they're the only
 *      role that can flip the AIHistory flag, so they need to see what they
 *      enabled.)
 *
 * Mirrors the pattern used by SyncToButton.readFlagFromJwt. JWT-based, so a
 * stale token after a server-side feature change still gates correctly until
 * the next login. UX-only; backend routes still enforce auth + practice
 * ownership independently.
 */
const ALLOWED_ROLES = new Set(["Practitioner", "SystemAdmin"]);

function decodedToken() {
  const token = sessionStorage.getItem("user");
  return token ? jwtDecode(token) : null;
}

function rolesOf(decoded) {
  const rawRoles = decoded?.role ?? decoded?.roles;
  return Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
}

export function userCanSeeClinicalHistory() {
  try {
    const decoded = decodedToken();
    if (!decoded) return false;

    const hasFeature = !!decoded?.features?.AIHistory;
    if (!hasFeature) return false;

    const hasAllowedRole = rolesOf(decoded).some((r) => ALLOWED_ROLES.has(r));
    return hasFeature && hasAllowedRole;
  } catch (_e) {
    return false;
  }
}

/**
 * Render-gate for the Documents section (referral letters, reports, sick
 * notes). Same AIHistory flag as the history UI, but Practitioner only: the
 * author of a document is always the person generating it, and a SystemAdmin
 * must not sign a clinical letter. Mirrors requirePractitioner in
 * backend/src/documentRoutes.js.
 */
export function userCanGenerateDocuments() {
  try {
    const decoded = decodedToken();
    if (!decoded?.features?.AIHistory) return false;
    return rolesOf(decoded).includes("Practitioner");
  } catch (_e) {
    return false;
  }
}
